'use client';
// Tiered Staking — three plans with time locks: 6% / 9% / 15% APR (from your session wallet)
import { useState } from 'react';
import { toast } from 'sonner';
import { Vault, PiggyBank, Lock, Unlock, Coins, Percent, Trophy, Clock3, Zap, Wallet as WalletIcon } from 'lucide-react';
import {
  fetchJson, usePoll, post, fmtAmt, fmtInt,
  SectionHeader, Mono, Spinner,
} from './shared';
import { useSession, ConnectPrompt } from './session';
import type { Net } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface StakeRow { address: string; staked: number; rewards: number; locked: number; wallet: string | null; }
interface StakingData {
  totalStaked: number; stakers: StakeRow[]; apr: number; minStake: number;
  tiers: Record<string, { label: string; apr: number; lockDays: number; minStake: number }>;
  tierTotals: Record<string, number>; avgApr: number; lockedTotal: number;
}

// Static Tailwind classes per plan (cannot be generated dynamically)
const TIER_ACTIVE: Record<string, string> = {
  emerald: 'ring-emerald-500/60 bg-emerald-500/5',
  teal: 'ring-teal-500/60 bg-teal-500/5',
  amber: 'ring-amber-500/60 bg-amber-500/5',
};

const TIER_CARDS: Array<{ id: string; label: string; apr: string; lock: string; min: number; tone: string; lockDays: number }> = [
  { id: 'flex', label: 'Flexible', apr: '6%', lock: 'No lock — withdraw anytime', min: 10, tone: 'emerald', lockDays: 0 },
  { id: 'lock90', label: '90-day lock', apr: '9%', lock: 'Quarterly term', min: 100, tone: 'teal', lockDays: 90 },
  { id: 'lock365', label: '365-day lock', apr: '15%', lock: 'Annual term — highest yield', min: 500, tone: 'amber', lockDays: 365 },
];

export function Staking({ net }: { net: Net }) {
  const { session, wallet, openAccount } = useSession();
  const { data, refresh } = usePoll<{ staking: StakingData }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=staking`), 3000, [net]);
  const [amount, setAmount] = useState('');
  const [tier, setTier] = useState('flex');
  const [busy, setBusy] = useState(false);
  const sel = wallet;
  const activeTier = TIER_CARDS.find(t => t.id === tier)!;

  const act = async (type: string, amt?: number) => {
    if (!session) { openAccount(); return; }
    setBusy(true);
    try {
      const r = await post<{ error?: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password, type, amount: amt, tier: type === 'stake' ? tier : undefined,
      });
      if (r.error) { toast.error(r.error); return; }
      toast.success(type === 'stake'
        ? `Stake transaction queued (${activeTier.label}) — confirms after mining`
        : type === 'unstake' ? 'Unstake in progress' : 'Claiming rewards');
      setAmount('');
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action failed');
    } finally { setBusy(false); }
  };

  const s = data?.staking;
  const stakeAmt = Number(amount) || 0;
  const minForTier = activeTier.min;

  if (!session) {
    return (
      <div className="space-y-5">
        <SectionHeader icon={Vault} title="AmanChain tiered staking" desc="Lock AMAN and earn 6% to 15% annually — three plans with time locks like term deposits, yield accruing every second from the network protocol" />
        <ConnectPrompt what="stake your AMAN" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionHeader icon={Vault} title="AmanChain tiered staking" desc="Lock AMAN and earn 6% to 15% annually — three plans with time locks like term deposits, yield accruing every second from the network protocol" />

      {/* Plan cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        {TIER_CARDS.map(t => (
          <button
            key={t.id} onClick={() => setTier(t.id)}
            className={`glass p-4 text-left transition ring-1 ${tier === t.id ? TIER_ACTIVE[t.tone] : 'ring-border hover:ring-primary/30'}`}
            aria-pressed={tier === t.id}
          >
            <div className="flex items-center justify-between">
              <div className="text-2xl font-black text-teal-400" dir="ltr">{t.apr}</div>
              {t.lockDays > 0 && <span className="inline-flex items-center gap-1 text-[0.6rem] rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/25 px-2 py-0.5"><Clock3 className="size-3" /> {t.lockDays} days</span>}
              {t.lockDays === 0 && <span className="inline-flex items-center gap-1 text-[0.6rem] rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/25 px-2 py-0.5"><Zap className="size-3" /> flexible</span>}
            </div>
            <div className="font-bold text-sm mt-1.5">{t.label} plan</div>
            <div className="text-[0.65rem] text-muted-foreground mt-0.5">{t.lock} · minimum {fmtInt(t.min)} AMAN</div>
            <div className="mt-2 text-[0.62rem] text-muted-foreground" dir="ltr">Currently staked: {fmtAmt(s?.tierTotals?.[t.id] ?? 0)} AMAN</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Staking unit */}
        <div className="lg:col-span-2 glass p-6 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-teal-500/10 ring-1 ring-teal-500/25 p-4 text-center">
              <Percent className="size-5 text-teal-400 mx-auto mb-1.5" />
              <div className="text-xl font-black text-teal-400" dir="ltr">{s ? `${(s.avgApr * 100).toFixed(1)}%` : '—'}</div>
              <div className="text-[0.62rem] text-muted-foreground">network average yield</div>
            </div>
            <div className="rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/25 p-4 text-center">
              <Coins className="size-5 text-emerald-400 mx-auto mb-1.5" />
              <div className="text-xl font-black text-emerald-400" dir="ltr">{fmtAmt(s?.totalStaked)}</div>
              <div className="text-[0.62rem] text-muted-foreground">total staked</div>
            </div>
            <div className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/25 p-4 text-center">
              <Trophy className="size-5 text-amber-400 mx-auto mb-1.5" />
              <div className="text-xl font-black text-amber-400" dir="ltr">{fmtInt(s?.stakers.length)}</div>
              <div className="text-[0.62rem] text-muted-foreground">active stakers</div>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold mb-1.5 block">Your wallet — session only</label>
            <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2.5 text-sm font-bold flex items-center gap-2">
              <WalletIcon className="size-4 text-primary" /> {session.name}
            </div>
          </div>

          {sel && (
            <div className="rounded-lg bg-muted/40 ring-1 ring-border p-3 grid grid-cols-3 gap-2 text-center text-xs">
              <div><div className="font-bold text-emerald-400" dir="ltr">{fmtAmt(sel.balance)}</div><div className="text-[0.6rem] text-muted-foreground">available</div></div>
              <div><div className="font-bold text-teal-400" dir="ltr">{fmtAmt(sel.staked)}</div><div className="text-[0.6rem] text-muted-foreground">staked</div></div>
              <div><div className="font-bold text-amber-400" dir="ltr">{fmtAmt(sel.stakeRewards, 6)}</div><div className="text-[0.6rem] text-muted-foreground">accrued rewards</div></div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold mb-1.5 block" htmlFor="st-amount">Amount (minimum for {activeTier.label}: {minForTier} AMAN)</label>
            <Input id="st-amount" type="number" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => act('stake', stakeAmt)} disabled={busy || stakeAmt < minForTier} className="flex-1 gap-2 font-bold">
              <Lock className="size-4" /> Stake ({activeTier.label})
            </Button>
            <Button variant="secondary" onClick={() => act('unstake', stakeAmt)} disabled={busy || !stakeAmt} className="flex-1 gap-2 font-bold">
              <Unlock className="size-4" /> Unstake unlocked
            </Button>
            <Button variant="secondary" onClick={() => act('claim_stake')} disabled={busy || !sel?.stakeRewards} className="flex-1 gap-2 font-bold">
              <PiggyBank className="size-4" /> Claim rewards
            </Button>
          </div>
          <p className="text-[0.65rem] text-muted-foreground leading-relaxed">
            Yield accrues every second on every deposit according to its plan (Flexible 6% · 90-day 9% · 365-day 15% APR).
            Locked deposits cannot be withdrawn before maturity — unstaking works through the oldest open deposits first.
            Claims and staking transactions pay the usual fee (50% burned).
          </p>
        </div>

        {/* Leaderboard */}
        <div className="glass p-5">
          <div className="text-sm font-bold mb-3 flex items-center gap-2"><Trophy className="size-4 text-amber-400" /> Top stakers</div>
          {!data && <Spinner />}
          <div className="space-y-2 max-h-96 overflow-y-auto pl-1">
            {(s?.stakers ?? []).map((r, i) => (
              <div key={r.address} className="rounded-lg border border-border/60 bg-muted/30 p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-bold flex items-center gap-1.5">
                    <span className={`rounded-full w-5 h-5 inline-flex items-center justify-center text-[0.6rem] font-black ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-slate-500/20 text-slate-600 dark:text-slate-300' : i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                    {r.wallet ?? <Mono>{r.address.slice(0, 12)}…</Mono>}
                  </div>
                  <div className="text-[0.62rem] text-muted-foreground mt-1" dir="ltr">
                    +{fmtAmt(r.rewards, 6)} rewards{r.locked > 0 ? ` · ${fmtAmt(r.locked)} locked` : ''}
                  </div>
                </div>
                <div className="text-sm font-black text-teal-400 shrink-0" dir="ltr">{fmtAmt(r.staked)}</div>
              </div>
            ))}
            {s && s.stakers.length === 0 && <div className="text-xs text-muted-foreground text-center py-8">No stakers yet — be the first to lock your coins</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
